import React from "react";
import { CHARGING_AGREEMENT_STATUS_MAP, ChargingAgreementStatus, ChargingAgreement, ChargingAgreementSortFields, ChargingAgreementType, ChargingStation } from "./models";
import {dateFormatOptions} from "../../common/dateFormatterConfig";
import Pagination from "rc-pagination";
import {locale} from "../../common/table/paginatorConfig";
import { SortIcons } from "components/SortIcons/SortIcons";
import { roundNumber } from "helpers";
import { IPageResponse, SortDirection } from "common/interfaces/IPageResponse";
import { Button, OverlayTrigger, Spinner, Tooltip } from "react-bootstrap";
import { isNotNullOrUndefined } from "common/data-util";

interface ChargingAgreementTableProps {
    chargingAgreements: IPageResponse<ChargingAgreement>;
    onPageChange: (page: number, size: number) => void;
    onSort: (sortField: ChargingAgreementSortFields) => void;
    sortField: ChargingAgreementSortFields;
    sortDirection: SortDirection;
    openEditChargingAgreementModal: (chargingAgreement: ChargingAgreement) => void;
    openCancelChargingAgreementModal: (chargingAgreement: ChargingAgreement) => void;
    getPdfPreview: (agreement: ChargingAgreement) => void;
    pdfPreviewLoading?: number
}

export function ChargingAgreementsTable(props: ChargingAgreementTableProps) {
    const chargingAgreements: Array<ChargingAgreement> = props.chargingAgreements.content;
    const pageResponse = props.chargingAgreements;

    const chargingAgreementTableRows = chargingAgreements.map((chargingAgreement: ChargingAgreement) =>
        <tr key={'row-' + chargingAgreement.id}>
            <td className="line-height-1">
                {chargingAgreement.customer.name}<br />
            </td>
            <td className="text-nowrap">
                {chargingAgreement.contractNumber}
            </td>
            <td>
                {chargingAgreement.startDate ? new Intl.DateTimeFormat('et', dateFormatOptions).format(new Date(chargingAgreement.startDate)) : ""}
            </td>
            <td>
                {chargingAgreement.endDate ? new Intl.DateTimeFormat('et', dateFormatOptions).format(new Date(chargingAgreement.endDate)) : 'Tähtajatu'}
            </td>            
            <td>
                {isNotNullOrUndefined(chargingAgreement.pricing.thirdPartyChargingMarginalCentsKwh) ? roundNumber(chargingAgreement.pricing.thirdPartyChargingMarginalCentsKwh, 4) : "-"}
            </td>
            <td>
                {chargingAgreement.meteringPoint.eic}
            </td>
            <td>
                {
                    chargingAgreement.type === ChargingAgreementType.ORGANIZATION
                        ? <ChargingStationIdCell chargingStations={chargingAgreement.chargingStations} id={chargingAgreement.id} />
                        : chargingAgreement.externalChargingStationId
                }
            </td>
            <td>
                {CHARGING_AGREEMENT_STATUS_MAP[chargingAgreement.status]}
            </td>
            <td>
                {chargingAgreement.dateCreated ? new Intl.DateTimeFormat('et', dateFormatOptions).format(new Date(chargingAgreement.dateCreated)) : ""}
            </td>
            <td>
                <div className="row__actions">
                {
                        (chargingAgreement.status === ChargingAgreementStatus.ACTIVE) && (
                            <OverlayTrigger
                                    delay={250}
                                    overlay={
                                        <Tooltip id={`update-package-tooltip-${chargingAgreement.id}`}>
                                            <p style={{margin: '3px'}}>Muuda lepingut</p>
                                        </Tooltip>
                                    } 
                                    placement={"left"}
                            >
                                <Button 
                                    size="sm"                             
                                    variant="secondary"
                                    className="d-flex align-items-center btn-outline-icon" 
                                    onClick={() => props.openEditChargingAgreementModal(chargingAgreement)}
                                    >
                                        <i className="icon--16 my-2 icon__edit--outline-mask"/>
                                </Button>
                            </OverlayTrigger>
                        )
                    }
                                        {
                        (chargingAgreement.status === ChargingAgreementStatus.PENDING) && (
                            <OverlayTrigger
                                    delay={250}
                                    overlay={
                                        <Tooltip id={`cancel-offer-tooltip-${chargingAgreement.id}`}>
                                            <p style={{margin: '3px'}}>Tühista pakkumine</p>
                                        </Tooltip>
                                    } 
                                    placement={"left"}
                            >
                                <Button 
                                    size="sm"                             
                                    variant="secondary"
                                    className="d-flex align-items-center btn-outline-icon" 
                                    onClick={() => props.openCancelChargingAgreementModal(chargingAgreement)}
                                    >
                                        <i className="icon--16 my-2 icon__cancel--outline-mask"/>
                                </Button>
                            </OverlayTrigger>
                        )
                    }
                    <OverlayTrigger delay={250}
                        overlay={
                            <Tooltip id={`tooltip-${chargingAgreement.id}`}>
                                <p style={{margin: '3px'}}>Vaata PDF faili</p>
                            </Tooltip>} placement={"left"}>
                        <Button 
                            size="sm"
                            variant="secondary"
                            className="d-flex align-items-center btn-outline-icon ml-1" 
                            onClick={() => props.getPdfPreview(chargingAgreement)}
                        >
                            {
                                props.pdfPreviewLoading === chargingAgreement.id ? (
                                    <Spinner animation="border" size="sm"/> 
                                ) : (
                                    <div className='icon__preview-file action-icon'/>
                                )
                            }
                        </Button>
                    </OverlayTrigger>
                </div>
            </td>
        </tr>
    );

    return (
        <table className='generic-table table table-responsive-md table-hover'>
            <thead>
            <tr>
                <th>Klient</th>
                <th>Lepingu number</th>
                <th className="sort-down-up-hover">
                    <div className="d-flex align-items-center"
                        onClick={() => props.onSort(ChargingAgreementSortFields.START_DATE)}>
                        <span>Alguskuupäev</span>
                        <SortIcons
                            sortedByField={props.sortField}
                            sortDirection={props.sortDirection}
                            currentSortColumn={ChargingAgreementSortFields.START_DATE} />
                    </div>
                </th>
                <th className="sort-down-up-hover">
                    <div className="d-flex align-items-center"
                        onClick={() => props.onSort(ChargingAgreementSortFields.END_DATE)}>
                        <span>Lõppkuupäev</span>
                        <SortIcons
                            sortedByField={props.sortField}
                            sortDirection={props.sortDirection}
                            currentSortColumn={ChargingAgreementSortFields.END_DATE} />
                    </div>
                </th>                
                <th>Teenuse marginaal, s/kWh</th>
                <th>Mõõtepunkt</th>
                <th>Jaama NOOM id</th>
                <th>Staatus</th>
                <th className="sort-down-up-hover">
                    <div className="d-flex align-items-center"
                        onClick={() => props.onSort(ChargingAgreementSortFields.DATE_CREATED)}>
                        <span>Leping loodud</span>
                        <SortIcons
                            sortedByField={props.sortField}
                            sortDirection={props.sortDirection}
                            currentSortColumn={ChargingAgreementSortFields.DATE_CREATED} />
                    </div>
                </th>
                <th></th>
            </tr>
            </thead>
            <tbody>
            {chargingAgreementTableRows}
            </tbody>
            <tfoot>
            <tr>
                <td colSpan={9}>
                    <div className="pagination-wrapper">
                        <Pagination
                            locale={locale}
                            current={pageResponse.number + 1}
                            total={pageResponse.totalElements}
                            pageSize={pageResponse.size}
                            prefixCls={"pagination"}
                            onChange={props.onPageChange}
                            showSizeChanger={false}
                            hideOnSinglePage={true}
                        />
                    </div>
                </td>
            </tr>
            </tfoot>
        </table>
    )
}

function ChargingStationIdCell({ chargingStations, id }: { chargingStations: ChargingStation[], id: number }) {
    const withIds = chargingStations.filter(s => !!s.externalId);

    const allIds = withIds.map(s => <small className="d-block line-height-1" key={s.id}>{s.externalId}</small>);
    if (withIds.length < 4) return <>{ allIds }</>;
    
    const sliced = withIds.slice(0, 3);
    const hidden = withIds.slice(3);

    return (
        <OverlayTrigger delay={250}
            overlay={
                <Tooltip id={`tooltip-csid-${id}`}>
                    <>{ allIds }</>
                </Tooltip>
            }
            placement={"top"}
        >
            <div>
                { sliced.map(s => <small className="d-block line-height-1 text-nowrap" key={s.id}>{s.externalId}</small>) }
                <small className="d-block line-height-1">...ja veel {hidden.length}</small>
            </div>
        </OverlayTrigger>
    );
}